import {
  StructureRepresentationBuiltInProps,
  createStructureRepresentationParams,
} from "molstar/lib/mol-plugin-state/helpers/structure-representation-params";
import { PluginCommands } from "molstar/lib/mol-plugin/commands";
import { Color } from "molstar/lib/mol-util/color";

type StructureType = "polymer" | "ligand" | "water" | "ion";

class MolecularStructure {
  private plugin: any;
  private structureRef: any;
  private name: string;

  /** Define a strict type for structure components */
  private components: Record<
    "polymer" | "ligand" | "water" | "ion",
    { structure: any; type: any,typeParams:any }
  >;

  constructor(plugin: any, name: string, structureRef: any) {
    this.plugin = plugin;
    this.structureRef = structureRef;
    this.name = name;

    this.components = {
      polymer: { structure: null, type: "cartoon",
      typeParams:{
        material:{
          roughness:0.2,
          metalness:0
        }
      },
       },
      ligand: { structure: null, type: "ball-and-stick",
      typeParams:{
        material:{
          roughness:0.2,
          metalness:0
        }
      },
       },
      water: { structure: null, type: "ball-and-stick",
      typeParams:{
        material:{
          roughness:0.2,
          metalness:0
        }
      },
       },
      ion: { structure: null, type: "ball-and-stick",
      typeParams:{
        material:{
          roughness:0.2,
          metalness:0
        }
      },
       },
    };
  }

  /** Apply representation to a structure component */
  async applyRepresentation(
    structureType: "polymer" | "ligand" | "water" | "ion",
    type: any
  ) {
    let selectedComponent = this.components[structureType]?.structure;
    if (!selectedComponent) return console.error(`${structureType} not found`);

    let props: StructureRepresentationBuiltInProps = {
      type: type,
      typeParams:{
        ...this.components[structureType].typeParams
      }
    };
    const newRepresentation = createStructureRepresentationParams(
      this.plugin,
      void 0,
      props
    );

    await this.plugin
      .build()
      .to(selectedComponent)
      .update(newRepresentation)
      .commit();

    this.components[structureType].type = type;
  }


/** Highlight the structure on hover*/
  async highlightComponent( structureType: "polymer" | "ligand" | "water" | "ion", highlight: boolean) {

    let selectedComponent = this.components[structureType]?.structure;
    if (!selectedComponent) return console.error(`${structureType} not found`);

    if (highlight) {
        await PluginCommands.Interactivity.Object.Highlight(this.plugin, {
            state: this.plugin.state.data,
            ref: selectedComponent.ref
        });
    } else {
        await PluginCommands.Interactivity.ClearHighlights(this.plugin);
    }
}

  /** Show or hide a structure component */
  async toggleVisibility(
    structureType: "polymer" | "ligand" | "water" | "ion",
    show: boolean
  ) {
    
    let selectedComponent = this.components[structureType]?.structure;
    if (!selectedComponent) return console.error(`${structureType} not found`);

    let props: StructureRepresentationBuiltInProps = {
      sizeParams: { value: show ? 1.5 : 0 },
      typeParams:{
        ...this.components[structureType].typeParams
      }
    };

    if(show){
        props.type = this.components[structureType].type
    }

    const newRepresentation = createStructureRepresentationParams(
      this.plugin,
      void 0,
      props
    );

    await this.plugin
      .build()
      .to(selectedComponent)
      .update(newRepresentation)
      .commit();
  }

  /** Update transparency of a structure component */
  async updateTransparency(
    structureType: "polymer" | "ligand" | "water" | "ion",
    transparency: number
  ) {
    let selectedComponent = this.components[structureType]?.structure;
    if (!selectedComponent) return console.error(`${structureType} not found`);

    let props: StructureRepresentationBuiltInProps = {
      typeParams:{
        ...this.components[structureType].typeParams,
        alpha:transparency
      },
      type: this.components[structureType].type,
    };

    const newRepresentation = createStructureRepresentationParams(
      this.plugin,
      void 0,
      props
    );

    await this.plugin
      .build()
      .to(selectedComponent)
      .update(newRepresentation)
      .commit();
  }

  /** Get structure name */
  getStructureName() {
    return this.name;
  }

  /** Get structure reference */
  getStructureRef() {
    return this.structureRef;
  }

  setBackground(color: number) {
    if (color === -1) {
      const renderer = this.plugin.canvas3d.props.renderer;
      PluginCommands.Canvas3D.SetSettings(this.plugin, {
        settings: { renderer: { ...renderer }, transparentBackground: true },
      });
      return;
    }
    if (!this.plugin.canvas3d) return;
    const renderer = this.plugin.canvas3d.props.renderer;
    PluginCommands.Canvas3D.SetSettings(this.plugin, {
      settings: {
        renderer: { ...renderer, backgroundColor: Color(color) },
        transparentBackground: false,
      },
    });
  }

  async view(mode: "perspective" | "orthographic") {
    if (!this.plugin.canvas3d) return;
    // Update the camera mode
    PluginCommands.Canvas3D.SetSettings(this.plugin, {
      settings: {
        camera: {
          ...this.plugin.canvas3d.props.camera,
          mode, // Change to "perspective" or "orthographic"
        },
      },
    });
  }
  
  async captureScreenshot() {
    if (!this.plugin.canvas3d || !this.plugin.canvas3d.webgl) {
      console.error("Canvas3D or WebGL context is not available.");
      return;
    }

    const webgl = this.plugin.canvas3d.webgl;
    const width = webgl.gl.drawingBufferWidth;
    const height = webgl.gl.drawingBufferHeight;

    // ✅ Get pixel data (this function returns the Uint8Array, no arguments needed)
    const pixelData = webgl.getDrawingBufferPixelData();

    if (!pixelData) {
      console.error("Failed to get pixel data.");
      return;
    }
    // Create ImageData from pixel buffer
    const imageData = new ImageData(
      new Uint8ClampedArray(pixelData.array),
      width,
      height
    );

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    // Draw the image data to the canvas
    ctx.putImageData(imageData, 0, 0);

    // Convert canvas to a downloadable image (PNG)
    canvas.toBlob((blob) => {
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "Dr_Viz_Structure.png"; // Set the desired file name
      link.click();
    }, "image/png");
  }

  /** Set structure component */
  setComponent(
    structureType: "polymer" | "ligand" | "water" | "ion",
    structure: any
  ) {
    if (this.components[structureType]) {
      this.components[structureType].structure = structure;
    }
  }

  /** Destructor to clean up structure */
  async destroy() {
    for (const key of Object.keys(this.components) as StructureType[]) {
      const component = this.components[key];
      if (component.structure) {
        // await this.plugin.state.data.delete(component.structure.ref);
        component.structure = null;
      }
    }
  }
}

export default MolecularStructure;
